import type { ConflictResult, ConflictRule, ConflictRuleContext } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';

function isoWeekdayFromDate(dateKey: string): number {
  // getUTCDay(): 0=Sun … 6=Sat → convert to 0=Mon … 6=Sun
  return (new Date(dateKey + 'T00:00:00Z').getUTCDay() + 6) % 7;
}

/**
 * Detects assignments where the shift's `rules.weekday` restriction excludes
 * the date's ISO weekday (0=Mon … 6=Sun).
 *
 * Requires `context.shiftWeekdays` — a map of shiftUnitId → valid weekdays.
 * Units absent from the map (or with an empty array) are unrestricted.
 * Returns empty results when context is missing.
 */
export class ShiftWeekdayConflictRule implements ConflictRule {
  readonly type = 'shift_invalid_weekday' as const;

  detect(schedule: ScheduleMap, _events: unknown[], context?: ConflictRuleContext): ConflictResult[] {
    const shiftWeekdays = context?.shiftWeekdays;
    if (!shiftWeekdays || shiftWeekdays.size === 0) return [];

    const results: ConflictResult[] = [];

    for (const [date, daySchedule] of Object.entries(schedule)) {
      const isoDay = isoWeekdayFromDate(date);
      for (const [personId, unitIds] of Object.entries(daySchedule)) {
        for (const unitId of unitIds) {
          const validDays = shiftWeekdays.get(unitId);
          if (!validDays || validDays.length === 0) continue;
          if (!validDays.includes(isoDay)) {
            results.push({
              type: 'shift_invalid_weekday',
              date,
              personId,
              eventLabel: 'Shift not valid on this weekday',
              calendarId: '',
              shiftUnitId: unitId,
            });
          }
        }
      }
    }

    return results;
  }
}
